import { Link } from "wouter";
import { Phone, MessageSquare, Heart, ShieldAlert, ExternalLink } from "lucide-react";

interface CrisisContact {
  name: string;
  number?: string;
  text?: string;
  description: string;
  hours: string;
  colour: string;
  url?: string;
}

const CRISIS_CONTACTS: CrisisContact[] = [
  {
    name: "Samaritans",
    number: "116 123",
    description: "Free, confidential emotional support for anyone in distress or struggling to cope — available 24 hours a day, 7 days a week.",
    hours: "24/7 · free from any phone",
    colour: "bg-green-50 border-green-200",
    url: "https://www.samaritans.org",
  },
  {
    name: "Shout Crisis Text Line",
    text: "Text SHOUT to 85258",
    description: "Free, confidential crisis support by text message for anyone in the UK who is struggling to cope.",
    hours: "24/7 · free text service",
    colour: "bg-teal-50 border-teal-200",
    url: "https://giveusashout.org",
  },
  {
    name: "NHS Mental Health Crisis",
    number: "111 (option 2)",
    description: "If you are experiencing a mental health crisis, call NHS 111 and select option 2 to speak to a trained mental health professional.",
    hours: "24/7",
    colour: "bg-blue-50 border-blue-200",
    url: "https://www.nhs.uk/mental-health/social-care-and-your-rights/mental-health-and-the-law/mental-health-crisis-help/",
  },
  {
    name: "MIND Infoline",
    number: "0300 123 3393",
    description: "Mental health information and advice for anyone experiencing mental health problems — including what to do in a crisis.",
    hours: "Mon–Fri 9am–6pm",
    colour: "bg-purple-50 border-purple-200",
    url: "https://www.mind.org.uk",
  },
  {
    name: "PAPYRUS — Young Person's Suicide Prevention",
    number: "0800 068 4141",
    text: "Text PAP to 85258",
    description: "Confidential suicide prevention advice for young people under 35, and anyone worried about a young person.",
    hours: "Mon–Fri 9am–midnight · Weekends 2pm–midnight · Bank holidays 2pm–midnight · Free",
    colour: "bg-yellow-50 border-yellow-200",
    url: "https://www.papyrus-uk.org",
  },
  {
    name: "Refuge — Domestic Abuse Helpline",
    number: "0808 2000 247",
    description: "National Domestic Abuse Helpline for women and children experiencing domestic abuse. Free, confidential, and available round the clock.",
    hours: "24/7 · free",
    colour: "bg-rose-50 border-rose-200",
    url: "https://www.nationaldahelpline.org.uk",
  },
  {
    name: "NSPCC — Child Protection",
    number: "0808 800 5000",
    text: "Text 88858",
    description: "If you are worried a child is being abused or neglected — contact the NSPCC helpline for advice, support, or to make a referral.",
    hours: "24/7 · free",
    colour: "bg-orange-50 border-orange-200",
    url: "https://www.nspcc.org.uk",
  },
  {
    name: "Childline",
    number: "0800 1111",
    description: "Free, confidential helpline for children and young people in the UK. Counsellors available around the clock.",
    hours: "24/7 · free",
    colour: "bg-sky-50 border-sky-200",
    url: "https://www.childline.org.uk",
  },
];

export default function PatientSafeguarding() {
  return (
    <div className="min-h-screen bg-[#FDFBF7] font-sans">
      <header className="bg-white border-b border-rose-100/50 sticky top-0 z-20 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16 gap-3">
          <Heart className="h-6 w-6 text-rose-500 fill-rose-50 shrink-0" />
          <span className="text-xl font-bold text-slate-800 tracking-tight">RheumHub</span>
          <span className="text-slate-300 hidden sm:block">·</span>
          <span className="text-sm text-slate-500 hidden sm:block">Crisis Support &amp; Safeguarding</span>
          <div className="ml-auto">
            <Link href="/" className="text-sm text-rose-600 hover:text-rose-800 font-medium">← Back</Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">Crisis Support &amp; Safeguarding</h1>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="h-5 w-5 text-red-600 shrink-0" />
            <p className="font-bold text-red-800">In an emergency, always call 999</p>
          </div>
          <p className="text-sm text-red-700 leading-relaxed">
            If you or someone else is in immediate danger — including from self-harm, a medical emergency, or violence — call <strong>999</strong> or go to your nearest A&amp;E immediately. Do not wait.
          </p>
        </div>

        <div>
          <p className="text-slate-600 leading-relaxed mb-2">
            Living with a long-term condition like rheumatoid arthritis or lupus can sometimes feel overwhelming. You might experience periods of low mood, anxiety, or find things very difficult to cope with.
          </p>
          <p className="text-slate-600 leading-relaxed">
            The services below are here to help — all are free, confidential, and available across the UK. You do not need a referral to contact any of them.
          </p>
        </div>

        <div className="space-y-4">
          {CRISIS_CONTACTS.map((contact) => (
            <div key={contact.name} className={`border rounded-2xl p-5 ${contact.colour}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-slate-900 text-lg">{contact.name}</h3>
                    {contact.url && (
                      <a
                        href={contact.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-slate-700"
                        aria-label={`Visit ${contact.name} website`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{contact.description}</p>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {contact.number && (
                      <a
                        href={`tel:${contact.number.replace(/\s/g, "")}`}
                        className="inline-flex items-center gap-1.5 font-semibold text-slate-900 text-base hover:underline"
                        aria-label={`Call ${contact.name} on ${contact.number}`}
                      >
                        <Phone className="h-4 w-4 shrink-0" />
                        {contact.number}
                      </a>
                    )}
                    {contact.text && (
                      <span className="inline-flex items-center gap-1.5 font-medium text-slate-700 text-sm">
                        <MessageSquare className="h-4 w-4 shrink-0" />
                        {contact.text}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{contact.hours}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="h-4 w-4 text-rose-400 shrink-0" />
            <p className="font-semibold text-slate-800 text-sm">Talk to your rheumatology team</p>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Your rheumatology nurse or consultant can also refer you to specialist psychological support, pain management programmes, and peer support groups. Don't hesitate to mention how you are feeling at your next appointment — it matters.
          </p>
        </div>

        <p className="text-xs text-slate-400 pb-4">
          This page is publicly accessible without sign-in. If you feel unsafe right now, please call 999 or speak to someone you trust.
        </p>
      </main>

      <footer className="bg-white border-t border-slate-200 py-6 mt-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs text-slate-400">
            For medical emergencies, always call <strong>999</strong>. For urgent medical advice, call <strong>NHS 111</strong>.
          </p>
        </div>
      </footer>
    </div>
  );
}
